import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, RotateCcw, Trash2, Trash, AlertTriangle, Users, FolderKanban } from "lucide-react";

export default function AdminTrashPage() {
    const [data, setData] = useState({ users: [], projects: [] });
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/trash");
            setData(r.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const restore = async (kind, id) => {
        try {
            await api.post(`/admin/trash/${kind}/${id}/restore`);
            toast.success("Restaurado");
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const purge = async (kind, id) => {
        if (!window.confirm("Eliminar definitivamente? Esta ação não pode ser desfeita.")) return;
        try {
            await api.delete(`/admin/trash/${kind}/${id}/purge`);
            toast.success("Eliminado");
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const fmtDeleted = (s) => s ? new Date(s).toLocaleString("pt-PT") : "—";

    return (
        <div className="space-y-8" data-testid="admin-trash-page">
            <div>
                <Link to="/app/super" className="label-caps inline-flex items-center gap-2" data-testid="back-to-super">
                    <ArrowLeft size={12} /> Super Admin
                </Link>
                <h1 className="font-display text-5xl text-[#0d1628] mt-4">Lixo</h1>
                <p className="text-[#52525b] mt-3 max-w-3xl">
                    Utilizadores e projetos eliminados ficam aqui durante 30 dias antes de serem
                    eliminados definitivamente. Pode restaurar ou purgar manualmente.
                </p>
            </div>

            <div className="border-l-4 border-[#b89b6f] bg-[#fdf8f0] p-4 flex items-start gap-3">
                <AlertTriangle size={18} className="text-[#b89b6f] mt-0.5" />
                <div className="text-sm text-[#52525b]">
                    Itens com mais de 30 dias são purgados automaticamente todos os dias às 04:00 UTC.
                </div>
            </div>

            <Section icon={Users} title="Utilizadores" items={data.users} kind="users" loading={loading}
                onRestore={restore} onPurge={purge} fmtDeleted={fmtDeleted} render={(u) => (
                    <>
                        <td className="p-3 text-[#0d1628]">{u.name || u.email}</td>
                        <td className="p-3 text-[#52525b]">{u.email}</td>
                        <td className="p-3 text-[#52525b] capitalize">{u.role}</td>
                        <td className="p-3 text-[#52525b]">{u.company_name || "—"}</td>
                    </>
                )} headers={["Nome", "Email", "Role", "Empresa"]}
            />

            <Section icon={FolderKanban} title="Projetos" items={data.projects} kind="projects" loading={loading}
                onRestore={restore} onPurge={purge} fmtDeleted={fmtDeleted} render={(p) => (
                    <>
                        <td className="p-3 text-[#0d1628]">{p.name}</td>
                        <td className="p-3 text-[#52525b]">{p.company_name || "—"}</td>
                        <td className="p-3 text-[#52525b]">Fase {p.current_phase || 1}</td>
                        <td className="p-3 text-[#52525b]">{(p.created_at || "").slice(0, 10)}</td>
                    </>
                )} headers={["Nome", "Empresa", "Fase", "Criado"]}
            />
        </div>
    );
}

function Section({ icon: Icon, title, items, kind, loading, onRestore, onPurge, fmtDeleted, render, headers }) {
    return (
        <section data-testid={`trash-${kind}`}>
            <div className="flex items-center gap-2 mb-3">
                <Icon size={16} className="text-[#b89b6f]" />
                <div className="label-caps">{title} <span className="text-[#52525b]">({items.length})</span></div>
            </div>
            <div className="border border-[#e4e4e7] overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-[#e4e4e7] bg-[#f5f3ee]">
                            {headers.map((h) => <th key={h} className="text-left p-3 label-caps">{h}</th>)}
                            <th className="text-left p-3 label-caps">Eliminado em</th>
                            <th className="text-right p-3 label-caps">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={headers.length + 2} className="p-4 text-center text-[#52525b]">A carregar...</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={headers.length + 2} className="p-4 text-center text-[#52525b]">Sem itens.</td></tr>
                        ) : items.map((it) => (
                            <tr key={it.id} className="border-b border-[#e4e4e7] last:border-0" data-testid={`trash-item-${it.id}`}>
                                {render(it)}
                                <td className="p-3 text-[#52525b]">{fmtDeleted(it.deleted_at)}</td>
                                <td className="p-3 text-right">
                                    <div className="flex gap-2 justify-end">
                                        <button type="button" onClick={() => onRestore(kind, it.id)} className="btn-outline text-xs" data-testid={`restore-${it.id}`}>
                                            <RotateCcw size={11} /> Restaurar
                                        </button>
                                        <button type="button" onClick={() => onPurge(kind, it.id)} className="btn-ghost text-xs text-[#b91c1c]" data-testid={`purge-${it.id}`}>
                                            <Trash size={11} /> Purgar
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}
